import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const COLORS = ["#0f172a", "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

export function BudgetTrendChart() {
    const [data, setData] = useState(null);
    const [months, setMonths] = useState(6);

    useEffect(() => {
        api.get(`/budgets/trend?months=${months}`).then((r) => setData(r.data));
    }, [months]);

    if (!data || data.budgets.length === 0) return null;

    const chartData = data.months.map((m) => {
        const row = { month: m };
        data.budgets.forEach((b) => {
            row[b.name] = b.spent_by_month[m] ?? 0;
        });
        return row;
    });

    const avgLine = data.budgets[0]?.budget_amount || 0;

    return (
        <div className="rounded-lg border border-stone-200 bg-white p-4 mb-4" data-testid="budget-trend-chart">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="eyebrow">Spend vs cap · last {months} months</div>
                    <p className="text-[12px] text-stone-500 mt-0.5">Dashed line = budget cap for the first budget.</p>
                </div>
                <div className="flex items-center gap-1">
                    {[3, 6, 12].map((m) => (
                        <button key={m} onClick={() => setMonths(m)}
                                data-testid={`trend-months-${m}`}
                                className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                                    months === m ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-400"
                                }`}>
                            {m}m
                        </button>
                    ))}
                </div>
            </div>
            <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                        <XAxis dataKey="month" tickFormatter={(v) => v?.slice(5)} fontSize={10} tick={{ fill: "#78716c" }} />
                        <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} fontSize={10} tick={{ fill: "#78716c" }} />
                        <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 11 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {avgLine > 0 && <ReferenceLine y={avgLine} stroke="#b91c1c" strokeDasharray="4 3" />}
                        {data.budgets.slice(0, 8).map((b, i) => (
                            <Line key={b.id} type="monotone" dataKey={b.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export function BudgetTemplatesButton({ onDone }) {
    const { hasRole } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(() => {
        api.get("/budgets/templates").then((r) => setTemplates(r.data.templates || []));
    }, []);
    useEffect(() => { load(); }, [load]);

    const apply = async (key) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/budgets/templates/${encodeURIComponent(key)}`);
            const skipMsg = data.skipped > 0 ? ` · ${data.skipped} skipped (already exists)` : "";
            if (data.created === 0 && data.skipped > 0) {
                toast.info(`All ${data.skipped} template budgets already exist`);
            } else {
                toast.success(`Created ${data.created} budget(s)${skipMsg}`);
            }
            onDone?.();
            setOpen(false);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!hasRole("admin", "finance")) return null;

    return (
        <div className="relative inline-block">
            <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}
                    data-testid="budget-templates-btn">
                <Sparkles size={12} className="mr-1.5" /> Use template
            </Button>
            {open && (
                <div className="absolute right-0 mt-2 z-30 w-72 rounded-lg border border-stone-200 bg-white shadow-lg p-1" data-testid="budget-templates-menu">
                    {templates.map((t) => (
                        <button key={t.key} onClick={() => apply(t.key)} disabled={busy}
                                className="w-full text-left px-3 py-2 rounded hover:bg-stone-50 transition-colors"
                                data-testid={`budget-template-${t.key.replace(/\s+/g, "-").toLowerCase()}`}>
                            <div className="text-sm font-medium text-stone-900">{t.key}</div>
                            <div className="text-[11px] text-stone-500 mt-0.5">
                                {t.budgets.length} budgets · {fmtMoney(t.budgets.reduce((s, b) => s + b.amount, 0))}/mo
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
